import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  Link,
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { canAccessAdminSurface } from "./auth/AdminRoute";
import { AuthNavButton } from "./auth/AuthNavButton";
import { CreditsBadge } from "./credits/CreditsBadge";
import { Breadcrumbs } from "./Breadcrumbs";
import { LogoMark } from "./LogoMark";
import { CommandPalette } from "./palette/Palette";
import ThemeToggle from "./ThemeToggle";
import { Button } from "./ui/Button";
import { CloseButton } from "./ui/CloseButton";
import { ToastProvider } from "./ui/Toast";
import { useCatalogEntryQuery } from "../lib/catalog";
import {
  getDashboardBreadcrumbParams,
  getDashboardBreadcrumbs,
} from "../lib/dashboardBreadcrumbs";
import {
  isCreateNavigationPath,
  isLibraryNavigationPath,
} from "../lib/dashboardNavigation";
import {
  queryClient,
  useDashboardSummaryQuery,
  useMeQuery,
  useProjectQuery,
} from "../lib/queryClient";
import { UploadQueueProvider } from "../lib/uploadQueue";
import styles from "./AppLayout.module.css";

const STORAGE_KEY = "popcorn-ready-theme";
const DEFAULT_THEME = "popcorn";
const VALID_THEMES = new Set(["popcorn", "popcorn-warm", "popcorn-night", "popcorn-studio"]);

// Primary workspace nav. Library groups the collection routes until PR 5 gives
// it a dedicated tab shell.
const PRIMARY_NAV = [
  {
    label: "Dashboard",
    to: "/dashboard",
    activePaths: ["/dashboard"],
  },
  {
    label: "Library",
    to: "/library",
    activePaths: ["/library", "/projects", "/assets"],
  },
  {
    label: "Activity",
    to: "/activity",
    activePaths: ["/activity", "/runs"],
  },
  {
    label: "Inspiration",
    to: "/inspiration",
    activePaths: ["/inspiration"],
  },
];

type MobileTabItem = {
  label: string;
  to: string;
  activePaths: readonly string[];
  icon: "library" | "create" | "activity" | "account";
  badge?: "activity";
};

const MOBILE_TABS: readonly MobileTabItem[] = [
  {
    label: "Library",
    to: "/library",
    activePaths: ["/library", "/projects", "/assets", "/outputs"],
    icon: "library",
  },
  {
    label: "Create",
    to: "/create",
    activePaths: ["/create"],
    icon: "create",
  },
  {
    label: "Activity",
    to: "/activity",
    activePaths: ["/activity", "/runs"],
    icon: "activity",
    badge: "activity",
  },
  {
    label: "Account",
    to: "/account",
    activePaths: ["/account", "/settings", "/faq", "/admin", "/inspiration"],
    icon: "account",
  },
];

function applyStoredTheme() {
  try {
    const theme = window.localStorage.getItem(STORAGE_KEY);
    if (VALID_THEMES.has(theme ?? "")) {
      document.documentElement.dataset.theme = theme ?? "";
    } else {
      document.documentElement.dataset.theme = DEFAULT_THEME;
    }
  } catch {
    document.documentElement.dataset.theme = DEFAULT_THEME;
  }
}

export function RootLayout() {
  return (
    <RootProviders>
      <Outlet />
    </RootProviders>
  );
}

export function AppLayout() {
  return (
    <div className="web-shell">
      <header className={`web-shell-header ${styles.publicHeader}`}>
        <Link className={`web-shell-brand ${styles.publicBrand}`} to="/">
          <LogoMark
            className={`web-shell-logo ${styles.publicLogo} ${styles.logoStyleGlow} ${styles.logoColorPopcornYellow}`}
          />
          <span className={styles.wordmarkRounded}>Popcorn Ready</span>
        </Link>
        <nav className="web-shell-nav" aria-label="Primary">
          <a href="/#workflow">Workflow</a>
          <a href="/#pricing">Pricing</a>
          <AuthNavButton />
        </nav>
      </header>
      {/* Non-landmark wrapper: each route owns its own <main> (HomePage,
          AuthForm, …), so this must not be a second <main> landmark. */}
      <div className="web-shell-body">
        <Outlet />
      </div>
      <footer className="web-shell-footer">
        <span className={`web-shell-footer-brand ${styles.footerWordmark}`}>
          Popcorn Ready
        </span>
        <ThemeToggle />
      </footer>
    </div>
  );
}

// In local dev (`vite dev`) an unauthenticated visitor still gets the dashboard
// via the API's hybrid "autopilot" identity; logging in takes over with the real
// session. Production builds (DEV=false) always require login.
const DEV_AUTOPILOT = import.meta.env.DEV;
const FALLBACK_TABLET_BREAKPOINT = "900px";
const FALLBACK_MOBILE_BREAKPOINT = "640px";

function getBreakpointQuery(token: string, fallback: string) {
  const breakpoint =
    getComputedStyle(document.documentElement).getPropertyValue(token).trim() ||
    fallback;
  return `(max-width: ${breakpoint})`;
}

export function AuthenticatedAppLayout() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // Mobile-only drawer state; on desktop the sidebar is always visible and the
  // toggle button is hidden, so this never becomes true there.
  const [navOpen, setNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const wasNavOpen = useRef(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const mobileNavQuery = window.matchMedia(
      getBreakpointQuery("--bp-tablet", FALLBACK_TABLET_BREAKPOINT),
    );
    const closeWhenDesktop = () => {
      if (!mobileNavQuery.matches) setNavOpen(false);
    };
    closeWhenDesktop();
    mobileNavQuery.addEventListener("change", closeWhenDesktop);
    return () => {
      mobileNavQuery.removeEventListener("change", closeWhenDesktop);
    };
  }, []);

  useEffect(() => {
    const phoneTabsQuery = window.matchMedia(
      getBreakpointQuery("--bp-mobile", FALLBACK_MOBILE_BREAKPOINT),
    );
    const closeWhenPhoneTabsTakeOver = () => {
      if (phoneTabsQuery.matches) setNavOpen(false);
    };
    closeWhenPhoneTabsTakeOver();
    phoneTabsQuery.addEventListener("change", closeWhenPhoneTabsTakeOver);
    return () => {
      phoneTabsQuery.removeEventListener("change", closeWhenPhoneTabsTakeOver);
    };
  }, []);

  useEffect(() => {
    if (navOpen) {
      sidebarRef.current?.focus();
    } else if (wasNavOpen.current) {
      menuButtonRef.current?.focus();
    }
    wasNavOpen.current = navOpen;
  }, [navOpen]);

  useEffect(() => {
    if (!navOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNavOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [navOpen]);
  const dashboardQueriesEnabled =
    auth.status !== "loading" &&
    (auth.status !== "unauthenticated" || DEV_AUTOPILOT);
  const breadcrumbParams = getDashboardBreadcrumbParams(location);
  const breadcrumbProjectQuery = useProjectQuery(
    breadcrumbParams.projectId ?? "",
    dashboardQueriesEnabled && Boolean(breadcrumbParams.projectId),
  );
  const breadcrumbAnchorQuery = useCatalogEntryQuery(
    breadcrumbParams.anchorEntryId ?? "",
    dashboardQueriesEnabled && Boolean(breadcrumbParams.anchorEntryId),
  );
  const breadcrumbItems = getDashboardBreadcrumbs(location, {
    projectName: breadcrumbProjectQuery.data?.project.name,
    anchorTitle: breadcrumbAnchorQuery.data?.entry.title,
  });
  const authScope = auth.user?.id ?? (DEV_AUTOPILOT ? "dev-autopilot" : auth.status);
  const meQuery = useMeQuery(authScope, {
    enabled: dashboardQueriesEnabled,
  });
  const dashboardSummaryQuery = useDashboardSummaryQuery(authScope, {
    enabled: dashboardQueriesEnabled,
  });
  const me = meQuery.data ?? null;
  const mobileActivityCount =
    dashboardSummaryQuery.data?.summary.activeRuns.filter((run) =>
      run.status === "queued" || run.status === "running" || Boolean(run.reviewGate)
    ).length ?? 0;
  const accountLabel = useMemo(() => {
    if (auth.isAnonymous) return "Guest account";
    if (auth.user?.email) return auth.user.email;
    if (me?.actor && typeof me.actor === "object" && me.actor.email) {
      return me.actor.email;
    }
    if (me?.isLocal || auth.status === "disabled") return "Local developer";
    return "Account";
  }, [auth.isAnonymous, auth.status, auth.user?.email, me]);

  const showAdmin = canAccessAdminSurface(auth);
  const canSignOut = auth.configured && auth.status === "authenticated";
  async function signOut() {
    if (!canSignOut) return;
    await auth.signOut();
    navigate("/login", { replace: true });
  }

  if (auth.status === "loading") {
    return (
      <div className="web-shell">
        <main className="web-shell-main">
          <h1>Checking session</h1>
          <p className="muted">Preparing your workspace.</p>
        </main>
      </div>
    );
  }

  if (auth.status === "unauthenticated" && !DEV_AUTOPILOT) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return (
    <div className={styles.shell}>
      <aside
        id="dashboard-sidebar"
        ref={sidebarRef}
        tabIndex={-1}
        className={
          navOpen ? `${styles.sidebar} ${styles.sidebarOpen}` : styles.sidebar
        }
      >
        <div className={styles.sidebarHeader}>
          <Link className={styles.brand} to="/dashboard">
            <LogoMark className={styles.logo} />
            <span>Popcorn Ready</span>
          </Link>
          <CloseButton
            className={styles.sidebarClose}
            aria-label="Close navigation menu"
            onClick={() => setNavOpen(false)}
          />
        </div>

        {/* Quiet tier on purpose: each screen's featured action (e.g. the
            launchpad hero) owns the single gold CTA. */}
        <Link
          className={
            isCreateNavigationPath(location.pathname)
              ? `${styles.createLink} ${styles.createLinkActive}`
              : styles.createLink
          }
          to="/create"
          aria-current={isCreateNavigationPath(location.pathname) ? "page" : undefined}
          onClick={() => setNavOpen(false)}
        >
          Create
        </Link>

        <nav className={styles.nav} aria-label="Dashboard">
          {PRIMARY_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/settings"}
              aria-current={
                item.label === "Library" && isLibraryNavigationPath(location.pathname)
                  ? "page"
                  : undefined
              }
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                (item.label === "Library"
                  ? isLibraryNavigationPath(location.pathname)
                  : isActive ||
                    item.activePaths.some((path) =>
                      path === "/"
                        ? location.pathname === path
                        : location.pathname.startsWith(path)
                    ))
                  ? `${styles.navLink} ${styles.active}`
                  : styles.navLink
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          {showAdmin ? (
            <nav className={styles.footerNav} aria-label="Admin">
              <span className={styles.footerLabel}>Admin</span>
              <NavLink
                to="/admin"
                onClick={() => setNavOpen(false)}
                className={({ isActive }) =>
                  isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                }
              >
                Workbench
              </NavLink>
              <NavLink
                to="/admin/evals"
                onClick={() => setNavOpen(false)}
                className={({ isActive }) =>
                  isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                }
              >
                Admin evals
              </NavLink>
            </nav>
          ) : null}

          <nav className={styles.footerNav} aria-label="Account">
            <span className={styles.footerLabel}>Account</span>
            <NavLink
              to="/account"
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
              }
            >
              Credits &amp; billing
            </NavLink>
            <NavLink
              to="/settings"
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
              }
            >
              Settings
            </NavLink>
            <NavLink
              to="/faq"
              onClick={() => setNavOpen(false)}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
              }
            >
              FAQs
            </NavLink>
          </nav>

          {/* Mobile drawer only - on desktop the account lives in the topbar. */}
          <div className={styles.drawerAccount}>
            <span className={styles.drawerAccountLabel} title={accountLabel}>
              {accountLabel}
            </span>
            {canSignOut ? (
              <Button variant="secondary" size="sm" onClick={() => void signOut()}>
                Log out
              </Button>
            ) : null}
          </div>
        </div>
      </aside>

      {navOpen ? (
        <button
          type="button"
          className={styles.scrim}
          aria-label="Close navigation menu"
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <div className={styles.content}>
        <header className={styles.topbar}>
          <button
            ref={menuButtonRef}
            type="button"
            className={styles.menuButton}
            aria-label="Open navigation menu"
            aria-expanded={navOpen}
            aria-controls="dashboard-sidebar"
            onClick={() => setNavOpen(true)}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d="M4 7h16M4 12h16M4 17h16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div className={styles.breadcrumbSlot}>
            <Breadcrumbs items={breadcrumbItems} />
          </div>
          <div className={styles.commandSlot}>
            <CommandPalette showAdminCommands={showAdmin} />
          </div>
          <div className={styles.account}>
            <CreditsBadge
              authScope={authScope}
              enabled={auth.status !== "unauthenticated" || DEV_AUTOPILOT}
            />
            <Link
              className={`${styles.accountLink} ${styles.desktopOnly}`}
              to="/account"
            >
              {accountLabel}
            </Link>
            {canSignOut ? (
              <Button
                className={styles.desktopOnly}
                variant="secondary"
                size="sm"
                onClick={() => void signOut()}
              >
                Log out
              </Button>
            ) : null}
          </div>
        </header>
        <main className={styles.routeFrame}>
          <Outlet />
        </main>
        <MobileTabBar activityCount={mobileActivityCount} />
      </div>
    </div>
  );
}

function MobileTabBar({ activityCount }: { activityCount: number }) {
  const location = useLocation();

  return (
    <nav className={styles.mobileTabBar} aria-label="Primary mobile">
      {MOBILE_TABS.map((item) => {
        const isActive =
          item.label === "Create"
            ? isCreateNavigationPath(location.pathname)
            : item.label === "Library"
              ? isLibraryNavigationPath(location.pathname)
              : item.activePaths.some((path) =>
                  location.pathname === path || location.pathname.startsWith(`${path}/`)
                );
        const className = [
          styles.mobileTab,
          isActive ? styles.mobileTabActive : "",
        ]
          .filter(Boolean)
          .join(" ");
        const badge =
          item.badge === "activity" && activityCount > 0
            ? Math.min(activityCount, 9).toString()
            : null;

        return (
          <Link
            key={item.to}
            to={item.to}
            className={className}
            aria-current={isActive ? "page" : undefined}
          >
            <span className={styles.mobileTabIconWrap}>
              <MobileTabIcon name={item.icon} />
              {badge ? <span className={styles.mobileTabBadge}>{badge}</span> : null}
            </span>
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

function MobileTabIcon({ name }: { name: MobileTabItem["icon"] }) {
  switch (name) {
    case "library":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 6.5h16M4 12h16M4 17.5h16" />
          <path d="M7 4.5v15M17 4.5v15" />
        </svg>
      );
    case "create":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "activity":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 13h4l2-6 4 10 2-4h4" />
        </svg>
      );
    case "account":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </svg>
      );
  }
}

export function RootProviders({ children }: { children: ReactNode }) {
  useLayoutEffect(() => {
    applyStoredTheme();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <UploadQueueProvider>{children}</UploadQueueProvider>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
